
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type AiPrayerAssistantSettings = Database['public']['Tables']['ai_prayer_assistant_settings']['Row'];
type AiPrayerAssistantSettingsInsert = Database['public']['Tables']['ai_prayer_assistant_settings']['Insert'];
type AiPrayerAssistantSettingsUpdate = Database['public']['Tables']['ai_prayer_assistant_settings']['Update'];

type AiPrayerRequest = Database['public']['Tables']['ai_prayer_requests']['Row'];
type AiPrayerRequestInsert = Database['public']['Tables']['ai_prayer_requests']['Insert'];
type AiPrayerRequestUpdate = Database['public']['Tables']['ai_prayer_requests']['Update'];

type AiPrayerFormSettings = Database['public']['Tables']['ai_prayer_form_settings']['Row'];

export const AiPrayerAssistantService = {
  // Assistant Settings
  async getAssistantSettings(userId: string): Promise<AiPrayerAssistantSettings | null> {
    const { data, error } = await supabase
      .from('ai_prayer_assistant_settings')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching assistant settings:', error);
      throw error;
    }

    return data;
  },

  async createAssistantSettings(settings: AiPrayerAssistantSettingsInsert): Promise<AiPrayerAssistantSettings> {
    const { data, error } = await supabase
      .from('ai_prayer_assistant_settings')
      .insert(settings)
      .select()
      .single();

    if (error) {
      console.error('Error creating assistant settings:', error);
      throw error;
    }

    return data;
  },

  async updateAssistantSettings(
    userId: string, 
    updates: AiPrayerAssistantSettingsUpdate
  ): Promise<AiPrayerAssistantSettings> {
    const { data, error } = await supabase
      .from('ai_prayer_assistant_settings')
      .update(updates)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) {
      console.error('Error updating assistant settings:', error);
      throw error;
    }

    return data;
  },

  // Prayer Requests
  async createPrayerRequest(request: AiPrayerRequestInsert): Promise<AiPrayerRequest> {
    const { data, error } = await supabase
      .from('ai_prayer_requests')
      .insert(request)
      .select()
      .single();

    if (error) {
      console.error('Error creating prayer request:', error);
      throw error;
    }

    return data;
  },

  async updatePrayerRequest(
    requestId: string, 
    updates: AiPrayerRequestUpdate
  ): Promise<AiPrayerRequest> {
    const { data, error } = await supabase
      .from('ai_prayer_requests')
      .update(updates)
      .eq('id', requestId)
      .select()
      .single();

    if (error) {
      console.error('Error updating prayer request:', error);
      throw error;
    }

    return data;
  },

  async getUserPrayerRequests(userId: string): Promise<AiPrayerRequest[]> {
    const { data, error } = await supabase
      .from('ai_prayer_requests')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching prayer requests:', error);
      throw error;
    }

    return data || [];
  },

  // Form Settings
  async getFormSettings(): Promise<AiPrayerFormSettings | null> {
    const { data, error } = await supabase
      .from('ai_prayer_form_settings')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching form settings:', error);
      throw error;
    }

    return data;
  },

  // Analytics
  async trackAnalytics(
    userId: string | null,
    actionType: string,
    metadata?: Record<string, any>
  ): Promise<void> {
    const { error } = await supabase
      .from('ai_prayer_analytics')
      .insert({
        user_id: userId,
        action_type: actionType,
        metadata: metadata || {}
      });

    if (error) {
      console.error('Error tracking analytics:', error);
      // Don't throw error for analytics - continue execution
    }
  }
};
